import { useState, useRef, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//Components
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import ResetSubUserPassword from 'Modules/users/.components/resetSubUserPassword';
import { NotificationManager } from 'Components/Notifications';
import { Icon } from "@mui/material";
import { Tooltip } from "@gpstrackit/react-controls";

//Views
import GeneralDetails from 'Modules/activities/.components/generalDetails';
import ChangeList from 'Modules/activities/.components/changeList';
import ChipStatus from 'Components/ChipStatus';
import Activities from "Modules/activities/.components/tab";

//Helpers
import { endpoints } from 'Core/defaultValues';

//Actions
import {
    changeListOpenModal,
    changeUsernameOpenModal,
    resetPasswordOpenModal,
    resetEmailOpenModal,
    resendInviteOpenModal,
    activitiesSearchOpenModal,
    searchEntityRequest
} from 'Redux/actions';

//Permission
import { HasPermission } from "Core/security"

// hook
// import { useTokenOneTimeHandler } from 'Core/hooks';

const UsersTab = (props) => {
    const { company, companyId, height, tacRequired, loading, handleAuthenticate } = props;

    const dispatch = useDispatch();
    const tableRef = useRef();
    const { messages } = useIntl();

    const { openModal } = useSelector(state => state.subUsersReserPasswordRedux);

    const user = useSelector(state => state.securityRedux.user);
    const generalDetailsOpenModal = useSelector(state => state.generalDetailsRedux.openModal);
    const changeListOpenModalA = useSelector(state => state.changeListRedux.openModal);
    const lang = useSelector(state => state.commonRedux.language);
    const dealers = useSelector(state => state.generalRedux.dealers);
    const openModalActivities = useSelector(state => state.activitiesTabRedux.openModal);
    //const { handleAuthenticate } =  useTokenOneTimeHandler(null, dealers);

    const [columns, setColumns] = useState([
        {
            key: "id",
            field: "id",
            title: messages['users_tab_id'],
            show: true,
            sort: true,
            export: true,
            width: 120,
            position: 1,
            functionString: 'return row?.id',
            code: "users_tab_id",
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "userName",
            field: "userName",
            title: messages['users_tab_subuser'],
            show: true,
            sort: true,
            width: 250,
            export: true,
            functionString: 'return row?.userName',
            code: "users_tab_subuser",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', width: 250, justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ width: 225, overflow: 'hidden', wordWrap: 'break-word' }}>
                            <span>{item?.userName}</span>
                        </div>
                        <div style={{ width: 25 }}>
                            <Tooltip title={messages['common_show_changes']}>
                                <Icon onClick={() => {
                                    dispatch(changeListOpenModal(true, { entityName: "SubUser", entityId: item.id, compId: companyId }))
                                }} style={{ cursor: 'pointer' }}>info</Icon>
                            </Tooltip>
                        </div>
                    </div>
                )
            }
        },
        {
            key: "email",
            field: "email",
            title: messages['users_tab_email'],
            show: true,
            sort: true,
            export: true,
            functionString: 'return row?.email',
            code: "users_tab_email",
            render: (item) => {
                return <span>{item?.email}</span>
            }
        },
        {
            key: "tacAccepted",
            field: "tacAccepted",
            title: messages['users_tab_verify_tc'],
            show: true,
            export: true,
            functionString: `
                let value = null
                let lang = "${lang}"
                let validation = ${tacRequired}
                if(validation){
                    if(row?.tacAccepted){
                        value = lang === "en" ? "Accepted" : "Aceptado"
                    } else {
                        value = lang === "en" ? "Pending" : "Pendiente"
                    }
                }else {
                    value = "N/A"
                }
                return value
            `,
            code: "users_tab_verify_tc",
            render: (item) => {
                return <span>{tacRequired ? item?.tacAccepted ? messages['users_tab_accepted'] : messages['users_tab_pending'] : 'N/A'}</span>
            }
        },
        {
            key: "registrationStatus",
            field: "registrationStatus",
            title: messages['account_overview_registration_status'],
            width: 200,
            show: true,
            sort: false,
            locked: false,
            resize: true,
            code: "account_overview_registration_status",
            render: (item) => {
                return (
                    <div style={{ width: 210, height: 25 }}>
                        <ChipStatus label={item?.registrationStatus} messages={messages} />
                    </div>
                )
            },
            export: true,
            functionString: 'return row?.registrationStatus'
        },
    ])
    const searchFields = ["userName", "email"]

    useEffect(() => {
        (!dealers || dealers.length === 0) && dispatch(searchEntityRequest({ key: "dealers" }));
    }, []);

    useEffect(() => {
        if (!loading && lang) {
            // Use functional updater to preserve preference-restored 'show' flags
            setColumns(prev => prev.map((item) => ({
                ...item,
                title: messages[item.code],
                render: item.field === "tacAccepted" ? (item) => {
                    return <span>{tacRequired ? item?.tacAccepted ? messages['users_tab_accepted'] : messages['users_tab_pending'] : 'N/A'}</span>
                } : item.render,
                functionString: item.field === "tacAccepted" ? `
                    let value = null
                    let lang = "${lang}"
                    let validation = ${tacRequired}
                    if(validation){
                        if(row?.tacAccepted){
                            value = lang === "en" ? "Accepted" : "Aceptado"
                        } else {
                            value = lang === "en" ? "Pending" : "Pendiente"
                        }
                    }else {
                        value = "N/A"
                    }
                    return value
            ` : item.functionString,
            })))
        }
    }, [loading, lang])

    let innerHeight = window.innerHeight

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW HANDLERS & CONFIGURATION
    // Extracted from JSX for readability
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Handle condition changes from search/filters
     * CRITICAL: Uses options object for getData() - NOT positional arguments
     */
    const handleChangeConditions = (tags, conditions, total, newColumns, rows, sort) => {
        let currentConditions = {}
        let currentTags = {}
        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
        }

        currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'userName' && item?.fields[0] !== 'companyId');

        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length > 0) {
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }
        let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.userName))
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        currentConditions.push({ fields: ["companyId"], terms: [companyId], exact: true, conditionOperator: "AND" });
        
        // FIX: Always forward newSort to getData so that preference-restored sort is
        // not lost when entering the tags-only branch (e.g., on first load with persisted data).
        // Previously the tags-only branch omitted newSort, causing sort to revert to default.
        if (!conditions && tags) {
            tableRef?.current?.getData({ newConditions: currentConditions, newColumns, newSort: sort });
        } else {
            tableRef?.current?.getData({ newRows: rows, newSort: sort, newConditions: currentConditions });
        }
    };

    /**
     * Context menu options for row actions
     */
    const getMenuOptions = () => {
        return [
            messages['users_tab_login_as_user'],
            messages['users_tab_resend_invite'],
            messages['users_tab_reset_user_passowrd'],
            messages['users_tab_reset_user_email'],
            messages['users_tab_chanage_username']
        ]
    };

    /**
     * Handle context menu click events
     */
    const handleClickMenu = (event, item, clickEvent) => {
        console.log("clickEvent", clickEvent);
        if (event === messages['users_tab_reset_user_passowrd']) {
            if (HasPermission(user, "subusrrp", "u")) {
                if (item?.registrationStatus !== "Active") {
                    NotificationManager.warning(
                        messages['users_tab_warning_message_reset_password'],
                        messages['users_tab_users'],
                        3000,
                    );
                } else {
                    let entity = { ...item, entityName: "SubUser" }
                    dispatch(resetPasswordOpenModal(entity))
                }
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['users_tab_users'],
                    5000,
                );
            }

        }
        if (event === messages['users_tab_reset_user_email']) {
            if (HasPermission(user, "rsteml", "u")) {
                let entity = { ...item, entityName: "SubUser" }
                dispatch(resetEmailOpenModal({ entity, callback: () => { tableRef?.current?.getData({}) } }))
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['users_tab_users'],
                    5000,
                );
            }
        }
        if (event === messages['users_tab_resend_invite']) {
            if (HasPermission(user, "rsndinvt", "u")) {
                if (item?.registrationStatus === "Active") {
                    NotificationManager.warning(
                        messages['users_tab_warning_message_resend'],
                        messages['users_tab_users'],
                        3000,
                    );
                } else {
                    let entity = { ...item, entityName: "SubUser" }
                    dispatch(resendInviteOpenModal({ entity, callback: () => { tableRef?.current?.getData({}) } }))
                }
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['users_tab_users'],
                    5000,
                );
            }
        }
        if (event === messages['users_tab_login_as_user']) {
            if (HasPermission(user, "lgsub")) {
                handleAuthenticate(clickEvent, company?.dealer?.id, item?.email, `PX_${company?.id}_${item?.id}`);
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['users_tab_users'],
                    5000,
                );
            }
        }
        if (event === messages['account_overview_change_username']) {
            if (HasPermission(user, "cmpcu", "u")) {
                dispatch(changeUsernameOpenModal({ currentUsername: item.userName, entityId: item.id, masterUser: false, tableRef: tableRef }))
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['users_tab_users'],
                    5000,
                );
            }
        }
    };

    /**
     * Bulk action options
     */
    const menuOptions = [
        {
            id: "login-as-user",
            label: messages['users_tab_login_as_user'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "lgsub"),
            action: (items, event) => {
                handleAuthenticate(event, company?.dealer?.id, items[0]?.email, `PX_${company?.id}_${items[0]?.id}`);
            }
        },
        {
            id: "change-username",
            label: messages['account_overview_change_username'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "cmpcu"),
            action: (items) => {
                dispatch(changeUsernameOpenModal({ currentUsername: items[0]?.userName, entityId: items[0]?.id, masterUser: false, tableRef: tableRef }))
            }
        }
    ];

    /**
     * Custom column configuration - activities icon
     */
    const activitiesColumn = {
        render: (row) => {
            return (
                <div>
                    <Tooltip title={messages['users_tab_show_activities']}>
                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                            if (HasPermission(user, "act")) {
                                dispatch(activitiesSearchOpenModal({ entityName: "SubUser", entityId: row.id }, "users-tab-modal"))
                            } else {
                                NotificationManager.warning(
                                    messages['common_error_permission'],
                                    messages['users_tab_users'],
                                    5000,
                                );
                            }
                        }} >
                            assignment
                        </Icon>
                    </Tooltip>
                </div>
            )
        }
    };

    // FIX: permanentConditions ensures companyId filter is ALWAYS included in every
    // getData() call (initial, refresh, sort, scroll), even when onChangeConditions
    // hasn't fired yet (no-preferences scenario). This prevents unfiltered queries
    // that would return all users across all companies.
    // See: BUG-ANALYSIS-no-preferences-mandatory-conditions.md
    const permanentConditions = useMemo(() => [
        { fields: ["companyId"], terms: [companyId], exact: true, conditionOperator: "AND" }
    ], [companyId]);

    return (
        <div className="users-tab" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                columns={columns}
                name={`pm-users-tab-${companyId}-v6`}
                entity="users-account"
                infiniteScroll={false}
                selectionConfig={{ showCounter: false }}
                permanentConditions={permanentConditions}
                graphqlConfig={{
                    function: "searchSubUsers",
                    sortType: "SubUserSortInput",
                    conditionsType: "SubUserSearchInput",
                    customQuery: "id sm firstName userName",
                    dynamicQuery: true,
                }}
                exportConfig={{
                    fileName: "users",
                }}
                uiConfig={{
                    hideFilters: true,
                    hideSplit: true,
                    hideAllInPagination: true,
                    topHeight: props?.single ? 286 : 200,
                    height: height && height + "px" || "500px",
                }}
                defaultsConfig={{
                    rowsPerPage: 25,
                    valuesPerPage: [10, 25, 50]
                }}
                onChangeColumns={(items) => setColumns(items)}
                tableButtons={['orderColumn', 'export']}
                searchConfig={{
                    conditions: ["userName", "email"],
                    optionValue: "id",
                    optionLabel: "userName",
                    custom: { fields: ["companyId"], terms: [companyId], exact: true },
                    fields: searchFields,
                }}
                asyncSelect
                onChangeConditions={handleChangeConditions}
                menuOptions={getMenuOptions}
                onClickMenu={handleClickMenu}
                options={menuOptions}
                customColumn={activitiesColumn}
            />
            {openModal && <ResetSubUserPassword />}
            {changeListOpenModalA && <ChangeList />}
            {generalDetailsOpenModal && <GeneralDetails />}
            {openModalActivities && <Activities noFilters2 noOpenSearch2 modalView companyName={company.name} companyId={companyId} companyUsername={company.username} height={innerHeight - 257} entity={"activitiesModalUsers"} instanceId="users-tab-modal" />}
        </div>
    )
}
export default UsersTab;